import dayjs from 'dayjs'
import { useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

// antd
import {
    Button,
    Checkbox,
    Col,
    Collapse,
    DatePicker,
    Divider,
    Empty,
    Form,
    message,
    Row,
    Select,
    theme,
    Typography,
} from 'antd'

// components
import ApiSelect from '../../../../../components/api-select/api-select'
import DropDownMultiple from '../../../../../components/dropDownMultiple'

// assets
import { ExpandIcon, SkillsIcon, ToolsIcon } from '../../../../../assets/icons'

// constants
import { MASTER_DATA_FIELD } from '../../../../../constants/masterData'

// store
import { loginState } from '../../../../login/login.slice'
import {
    addEducationDraft,
    updateEducationDraft,
} from '../../store/applicationDraft.slice'
import {
    changeModel,
    masterDataAddDropDown,
    masterDataDropDownList,
    profileState,
} from '../../../../profile/profile.slice'

const EducationForm = ({ onCancel, currentData }) => {
    const { useToken } = theme
    const { Text, Title } = Typography
    const { token } = useToken()

    const dispatch = useDispatch()

    const timeoutRef = useRef()
    const sessionTokenRef = useRef()

    const [form] = Form.useForm()

    const { modelType, masterDataDropDown, isNext } = useSelector(profileState)
    const { userProfile } = useSelector(loginState)

    const [skillsData, setSkillsData] = useState([])
    const [toolsData, setToolsData] = useState([])
    const [checked, setChecked] = useState(false)
    const [suggestions, setSuggestions] = useState([])
    const [searchValue, setSearchValue] = useState('')
    const [page, setPage] = useState(0)
    const [status, setStatus] = useState('loading')

    const initialValues = useMemo(() => {
        if (!currentData) {
            return {
                institute: null,
                degree: null,
                fieldOfStudy: null,
                location: null,
                start: null,
                end: null,
                skills: [],
                tools: [],
                currentStudy: false,
            }
        }

        return {
            institute: currentData?.institute
                ? {
                      label: currentData?.institute?.label,
                      value: currentData?.institute?.value,
                  }
                : null,
            degree: currentData?.degree
                ? {
                      label: currentData?.degree?.label,
                      value: currentData?.degree?.value,
                  }
                : null,
            fieldOfStudy: currentData?.fieldOfStudy
                ? {
                      label: currentData?.fieldOfStudy?.label,
                      value: currentData?.fieldOfStudy?.value,
                  }
                : null,
            location: currentData?.location || null,
            start: currentData?.startDate ? dayjs(currentData.startDate) : null,
            end: currentData?.endDate ? dayjs(currentData.endDate) : null,
            skills:
                currentData?.skills?.map((skill) => ({
                    label: skill?.label,
                    value: skill?.value,
                    logo: skill?.logo,
                })) || [],
            tools:
                currentData?.tools?.length > 0
                    ? currentData?.tools.map((data) => {
                          return {
                              label: data?.label,
                              value: data?.value,
                              logo: data?.logo,
                          }
                      })
                    : [],

            currentStudy: currentData.currentStudy || false,
        }
    }, [currentData])

    useMemo(() => {
        if (currentData) {
            setSkillsData(
                currentData?.skills?.map((skill) => ({
                    label: skill?.name,
                    value: skill?._id,
                })) || []
            )
            setToolsData(
                currentData?.tools?.map((tool) => ({
                    label: tool?.name,
                    value: tool?._id,
                    // logo: tool?.logo,
                })) || []
            )
            if (currentData?.currentStudy) {
                setChecked(currentData?.currentStudy)
            }
        }
    }, [currentData])

    const fetchOptions = async (search = '', pageNumber = 1, model) => {
        setStatus('loading')
        setSearchValue(search)

        try {
            dispatch(changeModel(model))
            const result = await dispatch(
                masterDataDropDownList({
                    search: search,
                    id: model,
                    skip: pageNumber,
                })
            ).unwrap() // ⬅️ Get the actual payload

            const newData = Array.isArray(result?.data) ? result.data : []

            setPage(pageNumber + 1)
            setStatus(newData.length === 0 ? 'noData' : 'loaded')
        } catch (error) {
            console.error('Dropdown fetch failed', error)
            setStatus('noData')
        }
    }

    const resetPage = () => {
        setPage(1)
    }

    const formatDatePicker = (value) => {
        return `${dayjs(value).format('DD MMM YY')}`
    }

    async function addMasterData() {
        const result = await dispatch(
            masterDataAddDropDown({
                addData: {
                    type: 'manual',
                    data: {
                        name: searchValue,
                        logo:
                            modelType === 'tools'
                                ? 'https://pms-bucket123.s3.amazonaws.com/Layer_1_1689237094.svg'
                                : modelType === 'institute'
                                ? 'https://pms-bucket123.s3.amazonaws.com/Group 35301_1689226177.svg'
                                : '',
                        createdBy: userProfile?._id,
                    },
                },
                model: modelType,
            })
        )
        const data = result?.payload?.data

        if (data) {
            const { success, message: errorMessage } = data
            if (success) {
                dispatch(
                    masterDataDropDownList({
                        search: searchValue,
                        id: modelType,
                    })
                )
            } else {
                if (errorMessage) {
                    message.error(errorMessage)
                } else {
                    message.error('Something went wrong, try again later.')
                }
            }
        }
    }

    // GOOGLE PLACE API INTEGRATION
    const loadSuggestions = async (inputValue) => {
        clearTimeout(timeoutRef.current)

        if (!inputValue || inputValue.trim().length <= 1) {
            setSuggestions([])
            return
        }

        timeoutRef.current = setTimeout(async () => {
            if (!sessionTokenRef.current) {
                sessionTokenRef.current =
                    new window.google.maps.places.AutocompleteSessionToken()
            }

            new window.google.maps.places.AutocompleteService().getPlacePredictions(
                {
                    input: inputValue,
                    sessionToken: sessionTokenRef.current,
                },
                (predictions, status) => {
                    if (
                        status ===
                        window.google.maps.places.PlacesServiceStatus
                            .ZERO_RESULTS
                    ) {
                        setSuggestions([])
                        return
                    }
                    if (
                        status !==
                            window.google.maps.places.PlacesServiceStatus.OK ||
                        !predictions
                    ) {
                        return
                    }
                    let newData =
                        predictions?.length > 0 &&
                        predictions.map((data) => {
                            return {
                                label: data?.description,
                                value: data?.description,
                            }
                        })
                    setSuggestions(newData)
                }
            )
        }, 350)
    }

    // DISABLE START DATE
    const disabledDateStart = (current) => {
        const endDate = form.getFieldValue('end')
        // Disable dates that are after the selected end date or dates that are in the future
        return (
            (endDate && current && current.valueOf() > endDate.valueOf()) ||
            (current && current.valueOf() > Date.now())
        )
    }
    // DISABLE END DATE

    const disabledDateEnd = (current) => {
        const startDate = form.getFieldValue('start')
        // Disable dates that are before the selected start date or dates that are in the past
        return startDate
            ? (current && current.valueOf() < startDate.valueOf()) ||
                  (current && current.valueOf() > Date.now())
            : current && current.valueOf() > Date.now()
    }
    // COLLAPSE MENU HEADER
    const CollapseLabel = ({ name, icon, description }) => (
        <Row
            align={'middle'}
            style={{
                columnGap: '20px',
                marginBottom: '18px',
                rowGap: '10px',
            }}
        >
            <Row
                style={{
                    height: '50px',
                    width: '50px',
                    borderRadius: '10px',
                    padding: '12px',
                    backgroundColor: token.colorPalette.baseColor.tertiary,
                }}
                align={'middle'}
                justify={'center'}
            >
                <div
                    style={{
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '30px',
                    }}
                >
                    {icon}
                </div>
            </Row>
            <div>
                <Title
                    style={{
                        // fontSize: token.fontSizeHeading4,
                        fontSize: '18px',
                    }}
                >
                    {name}
                </Title>
                <Text className="sub-title" style={{ fontSize: '15px' }}>
                    {description}
                </Text>
            </div>
        </Row>
    )

    const handleSaveDraft = async () => {
        try {
            const values = await form.validateFields()

            const entry = {
                _tempId: currentData?._tempId || crypto.randomUUID(),
                attachments: values.attachments || null,
                institute: values.institute || null,
                degree: values.degree || null,
                fieldOfStudy: values.fieldOfStudy || null,
                skills: values.skills || [],
                tools: values.tools || [],
                location: values.location || null,
                grade: values.grade || null,
                startDate: values.start?.valueOf() || '',
                endDate: values.end?.valueOf() || '',
                currentStudy: values.currentStudy || false,
                summary: values.summary,
                isCompleted: true,
            }

            if (currentData?._tempId) {
                dispatch(
                    updateEducationDraft({
                        _tempId: currentData._tempId,
                        data: entry,
                    })
                )
            } else {
                dispatch(addEducationDraft(entry))
            }
            onCancel(false)
        } catch (error) {
            console.log('🚀 ~ handleSaveDraft ~ error:', error)
        }
    }

    // COLLAPSE ITEMS
    const getItems = () => [
        {
            key: '1',
            label: (
                <CollapseLabel
                    name={`Add Skills ${
                        skillsData?.length > 0 ? `(${skillsData?.length})` : ''
                    }`}
                    icon={
                        <SkillsIcon
                            color={token.colorPalette.baseColor.white}
                        />
                    }
                    description="Please add the skills suitable to your expertise."
                />
            ),
            children: (
                <>
                    <ApiSelect
                        labelInValue={true}
                        // label="Search Skills*"
                        name={MASTER_DATA_FIELD.skills}
                        rules={[
                            {
                                required: true,
                                message: 'Please Select Field of skills',
                            },
                        ]}
                        placeholder="Search a Field of skills"
                        tagRender={() => null}
                        rootClassName={[
                            'p-0-multiSelect',
                            skillsData?.length > 0
                                ? 'p-0-multiselect-setMargin'
                                : '',
                        ]}
                        onClick={() => {
                            resetPage()
                            fetchOptions('', 0, MASTER_DATA_FIELD.skills)
                        }}
                        onSelect={(e, options) => {
                            const trimSkills = options?.key.trimStart()
                            if (trimSkills?.length > 0) {
                                setSkillsData([
                                    ...skillsData,
                                    { label: trimSkills, value: e },
                                ])
                            }
                        }}
                        onDeselect={(e) => {
                            const removeData = [...skillsData]
                            removeData.splice(
                                removeData.findIndex(
                                    (item) => item.label === e
                                ),
                                1
                            )
                            setSkillsData(removeData)
                        }}
                        mode={'multiple'}
                        onSearch={(value) => {
                            resetPage()
                            fetchOptions(value, 0, MASTER_DATA_FIELD.skills)
                        }}
                        onScrollBottom={() => {
                            fetchOptions(
                                searchValue,
                                page,
                                MASTER_DATA_FIELD.skills
                            )
                        }}
                        dropdownRender={(menu, handleScroll) => (
                            <DropDownMultiple
                                handleScroll={handleScroll}
                                menu={menu}
                                searchValue={searchValue}
                                name={MASTER_DATA_FIELD.skills}
                                addMasterData={() => addMasterData()}
                            />
                        )}
                        options={masterDataDropDown?.skills}
                        dropdownStatus={status}
                    />
                </>
            ),
        },
        {
            key: '2',
            label: (
                <CollapseLabel
                    name={`Add Tools ${
                        toolsData?.length > 0 ? `(${toolsData?.length})` : ''
                    }`}
                    icon={
                        <ToolsIcon color={token.colorPalette.baseColor.white} />
                    }
                    description="Please add tools suitable to your hands-on experience."
                />
            ),
            children: (
                <>
                    <ApiSelect
                        labelInValue={true}
                        // label="Search Skills*"
                        name={MASTER_DATA_FIELD.tools}
                        rules={[
                            {
                                required: true,
                                message: 'Please Select Field of tools',
                            },
                        ]}
                        placeholder="Search a Field of tools"
                        tagRender={() => null}
                        rootClassName={[
                            'p-0-multiSelect',
                            skillsData?.length > 0
                                ? 'p-0-multiselect-setMargin'
                                : '',
                        ]}
                        onClick={() => {
                            resetPage()
                            fetchOptions('', 0, MASTER_DATA_FIELD.tools)
                        }}
                        onSelect={(e, options) => {
                            const trimSkills = options?.key.trimStart()
                            if (trimSkills?.length > 0) {
                                setToolsData([
                                    ...toolsData,
                                    { label: trimSkills, value: e },
                                ])
                            }
                        }}
                        onDeselect={(e) => {
                            const removeData = [...toolsData]
                            removeData.splice(
                                removeData.findIndex(
                                    (item) => item.label === e
                                ),
                                1
                            )
                            setToolsData(removeData)
                        }}
                        mode={'multiple'}
                        onSearch={(value) => {
                            resetPage()
                            fetchOptions(value, 0, MASTER_DATA_FIELD.tools)
                        }}
                        onScrollBottom={() => {
                            fetchOptions(
                                searchValue,
                                page,
                                MASTER_DATA_FIELD.tools
                            )
                        }}
                        dropdownRender={(menu, handleScroll) => (
                            <DropDownMultiple
                                handleScroll={handleScroll}
                                menu={menu}
                                searchValue={searchValue}
                                name={MASTER_DATA_FIELD.tools}
                                addMasterData={() => addMasterData()}
                            />
                        )}
                        options={masterDataDropDown?.tools}
                        dropdownStatus={status}
                    />
                </>
            ),
        },
    ]

    return (
        <div className="candidate-section">
            <Form
                labelCol={{
                    style: {
                        padding: '0 0 3px',
                    },
                }}
                layout="vertical"
                initialValues={initialValues}
                name="addEducation"
                requiredMark={true}
                form={form}
                style={{
                    marginBottom: '20px',
                }}
            >
                <Row
                    style={{
                        rowGap: '20px',
                    }}
                    justify={'space-between'}
                    gutter={16}
                >
                    <Col span={12}>
                        <ApiSelect
                            labelInValue={true}
                            label="Institute"
                            name={MASTER_DATA_FIELD.institute}
                            rules={[
                                {
                                    required: true,
                                    message: 'Please Select Field of institute',
                                },
                            ]}
                            placeholder="Search a Field of institute"
                            onClick={() => {
                                resetPage()
                                fetchOptions('', 0, MASTER_DATA_FIELD.institute)
                            }}
                            onSearch={(value) => {
                                resetPage()
                                fetchOptions(
                                    value,
                                    0,
                                    MASTER_DATA_FIELD.institute
                                )
                            }}
                            onScrollBottom={() => {
                                fetchOptions(
                                    searchValue,
                                    page,
                                    MASTER_DATA_FIELD.institute
                                )
                            }}
                            dropdownRender={(menu, handleScroll) => (
                                <DropDownMultiple
                                    handleScroll={handleScroll}
                                    menu={menu}
                                    searchValue={searchValue}
                                    name={MASTER_DATA_FIELD.institute}
                                    addMasterData={() => addMasterData()}
                                />
                            )}
                            options={masterDataDropDown?.institute}
                            dropdownStatus={status}
                            isNext={isNext}
                        />
                    </Col>

                    <Col span={12}>
                        <ApiSelect
                            labelInValue={true}
                            label="Degree"
                            name={MASTER_DATA_FIELD.degree}
                            rules={[
                                {
                                    required: true,
                                    message: 'Please Select Field of degree',
                                },
                            ]}
                            placeholder="Search a Field of degree"
                            onClick={() => {
                                resetPage()
                                fetchOptions('', 0, MASTER_DATA_FIELD.degree)
                            }}
                            onSearch={(value) => {
                                resetPage()
                                fetchOptions(value, 0, MASTER_DATA_FIELD.degree)
                            }}
                            onScrollBottom={() => {
                                fetchOptions(
                                    searchValue,
                                    page,
                                    MASTER_DATA_FIELD.degree
                                )
                            }}
                            dropdownRender={(menu, handleScroll) => (
                                <DropDownMultiple
                                    handleScroll={handleScroll}
                                    menu={menu}
                                    searchValue={searchValue}
                                    name={MASTER_DATA_FIELD.degree}
                                    addMasterData={() => addMasterData()}
                                />
                            )}
                            options={masterDataDropDown?.degree}
                            dropdownStatus={status}
                        />
                    </Col>
                    <Col span={12}>
                        <ApiSelect
                            labelInValue={true}
                            label="Field of Study"
                            name={MASTER_DATA_FIELD.fieldOfStudy}
                            rules={[
                                {
                                    required: true,
                                    message: 'Please Select Field of Study',
                                },
                            ]}
                            placeholder="Search a Field of Study"
                            onClick={() => {
                                resetPage()
                                fetchOptions(
                                    '',
                                    0,
                                    MASTER_DATA_FIELD.fieldOfStudy
                                )
                            }}
                            onSearch={(value) => {
                                resetPage()
                                fetchOptions(
                                    value,
                                    0,
                                    MASTER_DATA_FIELD.fieldOfStudy
                                )
                            }}
                            onScrollBottom={() => {
                                fetchOptions(
                                    searchValue,
                                    page,
                                    MASTER_DATA_FIELD.fieldOfStudy
                                )
                            }}
                            dropdownRender={(menu, handleScroll) => (
                                <DropDownMultiple
                                    handleScroll={handleScroll}
                                    menu={menu}
                                    searchValue={searchValue}
                                    name={MASTER_DATA_FIELD.fieldOfStudy}
                                    addMasterData={() => addMasterData()}
                                />
                            )}
                            options={masterDataDropDown?.fieldOfStudy}
                            dropdownStatus={status}
                        />
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Location"
                            name="location"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please Select Location',
                                },
                            ]}
                            colon={false}
                        >
                            <Select
                                placeholder="Search a Location"
                                allowClear={false}
                                notFoundContent={
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description="No Locations Searched"
                                    ></Empty>
                                }
                                onSearch={(value) => {
                                    loadSuggestions(value)
                                }}
                                showSearch
                                options={suggestions}
                                getPopupContainer={(trigger) =>
                                    trigger.parentNode
                                }
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Start Date"
                            colon={false}
                            name={'start'}
                            rules={[
                                {
                                    required: true,
                                    message: 'Please Select Start Date',
                                },
                            ]}
                        >
                            <DatePicker
                                allowClear={false}
                                name="start"
                                disabledDate={disabledDateStart}
                                format={formatDatePicker}
                                // getPopupContainer={(trigger) =>
                                //     trigger.parentNode
                                // }
                                getPopupContainer={() => document.body}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="End Date"
                            colon={false}
                            name={'end'}
                            rules={
                                !checked
                                    ? [
                                          {
                                              required: true,
                                              message: 'Please Select End Date',
                                          },
                                      ]
                                    : []
                            }
                        >
                            <DatePicker
                                allowClear={false}
                                disabled={checked}
                                disabledDate={disabledDateEnd}
                                name="end"
                                format={formatDatePicker}
                                // getPopupContainer={(trigger) =>
                                //     trigger.parentNode
                                // }
                                getPopupContainer={() => document.body}
                            />
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item
                            name="currentStudy"
                            valuePropName="checked"
                            rules={
                                !checked
                                    ? [
                                          {
                                              required: false,
                                              message: 'Please Select End Date',
                                          },
                                      ]
                                    : []
                            }
                        >
                            <Checkbox
                                onChange={(e) => {
                                    setChecked(e.target.checked)
                                    if (e.target.checked === true) {
                                        form.setFields([
                                            {
                                                name: 'end',
                                                errors: [],
                                            },
                                        ])
                                    }
                                    form.setFieldValue('end', '')
                                }}
                                checked={checked}
                            >
                                I currently study here
                            </Checkbox>
                        </Form.Item>
                    </Col>
                </Row>
                <Divider />
                <Collapse
                    bordered={false}
                    defaultActiveKey={['1', '2']}
                    expandIcon={({ isActive }) => {
                        return <ExpandIcon isActive={!isActive} />
                    }}
                    expandIconPosition="right"
                    style={{
                        backgroundColor: 'transparent',
                    }}
                    items={getItems()}
                />
                <Divider style={{ marginTop: '0px' }} />
                <div
                    style={{
                        marginTop: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'end',
                        columnGap: '20px',
                    }}
                >
                    <Button
                        variant="outlined"
                        style={{
                            color: '#000',
                            backgroundColor: 'transparent',
                            border: '1px solid transparent',
                            boxShadow: 'none',
                            height: '40px',
                            padding: '0px',
                        }}
                        color="default"
                        onClick={() => onCancel(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        style={{ height: '40px' }}
                        onClick={handleSaveDraft}
                    >
                        {currentData ? 'Save Change' : ' Add'}
                    </Button>
                </div>
            </Form>
        </div>
    )
}

export default EducationForm
